import React, { useState, useRef } from "react";
import api, { formatVND } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Crown, Star, Search, Upload, Plus, Gift, Coins, Diamond, Phone, Settings2,
  Pencil, Trash2, ToggleRight, ToggleLeft, Zap, Car, Coffee, Film, Dumbbell,
  BarChart3, AlertTriangle, TrendingDown, Sparkles, RefreshCw,
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar,
} from "recharts";

const TIER_CFG = {
  diamond: { label: "Diamond", color: "text-cyan-300", bg: "bg-cyan-500/10", border: "border-cyan-500/30", icon: Diamond },
  vip: { label: "VIP", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: Crown },
  normal: { label: "Thường", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/30", icon: Star },
};

const SOURCE_CFG = {
  pickleball: { label: "Pickleball", icon: Zap, color: "text-teal-400" },
  cafe: { label: "Cafe", icon: Coffee, color: "text-amber-400" },
  gym: { label: "Gym", icon: Dumbbell, color: "text-sky-400" },
  cinema: { label: "Cinema", icon: Film, color: "text-purple-400" },
  yougo: { label: "YouGo", icon: Car, color: "text-lime-400" },
  yougo_food: { label: "YouGo Food", icon: Coffee, color: "text-orange-400" },
  all: { label: "Tất cả", icon: Star, color: "text-slate-400" },
};

const REWARD_TYPES = [
  { value: "discount_vnd", label: "Giảm giá (VNĐ)" },
  { value: "free_item", label: "Tặng sản phẩm" },
  { value: "free_ship", label: "Miễn phí ship" },
  { value: "discount_percent", label: "Giảm giá (%)" },
];

export default function LoyaltyPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("profiles"); // profiles | rules | analytics
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [page, setPage] = useState(1);
  const [earnOpen, setEarnOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [earnPhone, setEarnPhone] = useState("");
  const [earnAmount, setEarnAmount] = useState("");
  const [earnSource, setEarnSource] = useState("pickleball");
  const [redeemPhone, setRedeemPhone] = useState("");
  const [redeemPoints, setRedeemPoints] = useState("");
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  // Rule form
  const [rName, setRName] = useState("");
  const [rDesc, setRDesc] = useState("");
  const [rSource, setRSource] = useState("all");
  const [rPoints, setRPoints] = useState("");
  const [rRewardType, setRRewardType] = useState("discount_vnd");
  const [rRewardValue, setRRewardValue] = useState("");
  const [rRewardLabel, setRRewardLabel] = useState("");
  const [rMaxMonth, setRMaxMonth] = useState("");
  const [rMinTier, setRMinTier] = useState("normal");

  const { data: statsData } = useQuery({
    queryKey: ["loyalty-stats"],
    queryFn: () => api.get("/loyalty/stats").then(r => r.data),
  });
  const { data: profilesData, isLoading } = useQuery({
    queryKey: ["loyalty-profiles", search, tierFilter, page],
    queryFn: () => api.get(`/loyalty/profiles?search=${search}&tier=${tierFilter}&page=${page}&limit=30`).then(r => r.data),
    enabled: tab === "profiles",
  });
  const { data: rulesData } = useQuery({
    queryKey: ["loyalty-rules"],
    queryFn: () => api.get("/loyalty/rules").then(r => r.data),
    enabled: tab === "rules",
  });
  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ["loyalty-analytics"],
    queryFn: () => api.get("/loyalty/analytics").then(r => r.data),
    enabled: tab === "analytics",
  });

  const profiles = profilesData?.profiles || [];
  const total = profilesData?.total || 0;
  const stats = statsData || {};
  const rules = rulesData?.rules || [];
  const analytics = analyticsData || {};

  const [rfmRunning, setRfmRunning] = useState(false);
  const [seedRunning, setSeedRunning] = useState(false);
  const handleSeedSample = async () => {
    if (!window.confirm("Tạo 50 profiles mẫu + 300+ giao dịch cho demo Analytics?\n(Chỉ dùng khi chưa có dữ liệu)")) return;
    setSeedRunning(true);
    try {
      const { data } = await api.post("/loyalty/seed-sample-data");
      toast.success(data.message);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["loyalty-analytics"] });
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi tạo dữ liệu mẫu"); }
    finally { setSeedRunning(false); }
  };
  const handleRunRFM = async () => {
    setRfmRunning(true);
    try {
      const { data } = await api.post("/loyalty/run-rfm");
      toast.success(data.message);
      invalidate();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setRfmRunning(false); }
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["loyalty-profiles"] });
    queryClient.invalidateQueries({ queryKey: ["loyalty-stats"] });
    queryClient.invalidateQueries({ queryKey: ["loyalty-rules"] });
  };

  const handleEarn = async () => {
    if (!earnPhone || !earnAmount) return;
    try {
      const { data } = await api.post("/loyalty/earn", { phone: earnPhone, amount_vnd: parseInt(earnAmount), source: earnSource });
      toast.success(data.message); setEarnOpen(false); setEarnPhone(""); setEarnAmount(""); invalidate();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleRedeem = async () => {
    if (!redeemPhone || !redeemPoints) return;
    try {
      const { data } = await api.post("/loyalty/redeem", { phone: redeemPhone, points: parseInt(redeemPoints) });
      toast.success(data.message); setRedeemOpen(false); setRedeemPhone(""); setRedeemPoints(""); invalidate();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleImport = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/loyalty/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message); setImportOpen(false); invalidate();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi import"); }
    finally { setImporting(false); }
  };

  const openRuleDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setRName(rule.name); setRDesc(rule.description); setRSource(rule.source);
      setRPoints(String(rule.points_required)); setRRewardType(rule.reward_type);
      setRRewardValue(String(rule.reward_value)); setRRewardLabel(rule.reward_label);
      setRMaxMonth(String(rule.max_per_month || "")); setRMinTier(rule.min_tier);
    } else {
      setEditingRule(null);
      setRName(""); setRDesc(""); setRSource("all"); setRPoints(""); setRRewardType("discount_vnd");
      setRRewardValue(""); setRRewardLabel(""); setRMaxMonth(""); setRMinTier("normal");
    }
    setRuleDialogOpen(true);
  };

  const handleSaveRule = async () => {
    if (!rName || !rPoints) return;
    const body = {
      name: rName, description: rDesc, source: rSource,
      points_required: parseInt(rPoints), reward_type: rRewardType,
      reward_value: parseInt(rRewardValue || "0"), reward_label: rRewardLabel,
      max_per_month: parseInt(rMaxMonth || "0"), min_tier: rMinTier,
    };
    try {
      if (editingRule) {
        await api.put(`/loyalty/rules/${editingRule.id}`, body);
        toast.success("Cập nhật quy tắc thành công");
      } else {
        await api.post("/loyalty/rules", body);
        toast.success("Tạo quy tắc thành công");
      }
      setRuleDialogOpen(false); invalidate();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Xóa quy tắc này?")) return;
    try {
      await api.delete(`/loyalty/rules/${ruleId}`);
      toast.success("Đã xóa"); invalidate();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleToggleRule = async (rule) => {
    try {
      await api.put(`/loyalty/rules/${rule.id}`, { active: !rule.active });
      toast.success(rule.active ? "Đã tắt quy tắc" : "Đã bật quy tắc"); invalidate();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="loyalty-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Crown className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-[#F8FAFC]">CRM Loyalty</h1>
            <p className="text-slate-500 text-[11px]">Hệ sinh thái điểm thưởng Karinox</p>
          </div>
        </div>
        <div className="flex gap-2">
          {tab === "profiles" && (
            <>
              <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} className="border-[#334155] text-slate-400 gap-1.5 text-xs">
                <Upload className="w-3.5 h-3.5" /> Import
              </Button>
              <Button size="sm" onClick={() => setEarnOpen(true)} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold gap-1.5 text-xs">
                <Plus className="w-3.5 h-3.5" /> Tích điểm
              </Button>
              <Button size="sm" onClick={() => setRedeemOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-semibold gap-1.5 text-xs">
                <Gift className="w-3.5 h-3.5" /> Đổi điểm
              </Button>
            </>
          )}
          {tab === "rules" && (
            <Button size="sm" onClick={() => openRuleDialog()} className="bg-purple-500 hover:bg-purple-400 text-white font-semibold gap-1.5 text-xs">
              <Plus className="w-3.5 h-3.5" /> Thêm quy tắc
            </Button>
          )}
          {tab === "analytics" && (
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSeedSample} disabled={seedRunning} data-testid="seed-sample-btn"
                variant="outline" className="border-[#334155] text-slate-400 gap-1.5 text-xs">
                <Sparkles className={`w-3.5 h-3.5 ${seedRunning ? "animate-spin" : ""}`} /> {seedRunning ? "Đang tạo..." : "Tạo dữ liệu mẫu"}
              </Button>
              <Button size="sm" onClick={handleRunRFM} disabled={rfmRunning} data-testid="run-rfm-btn"
                className="bg-cyan-500 hover:bg-cyan-400 text-[#0f172a] font-semibold gap-1.5 text-xs">
                <RefreshCw className={`w-3.5 h-3.5 ${rfmRunning ? "animate-spin" : ""}`} /> {rfmRunning ? "Đang chạy..." : "Chạy RFM AI"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <div className="text-xs text-slate-500">Tổng thành viên</div>
          <div className="text-2xl font-bold text-[#F8FAFC] mt-1">{stats.total_profiles || 0}</div>
          <div className="flex gap-2 mt-2 text-[10px]">
            <span className="text-cyan-400">{stats.diamond || 0} Diamond</span>
            <span className="text-amber-400">{stats.vip || 0} VIP</span>
            <span className="text-slate-500">{stats.normal || 0} Thường</span>
          </div>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <div className="text-xs text-slate-500">Điểm lưu hành</div>
          <div className="text-2xl font-bold text-lime-400 mt-1">{formatVND(stats.total_points_active || 0)}</div>
          <div className="text-[10px] text-slate-600 mt-2">= {formatVND((stats.total_points_active || 0) * 1000)}đ</div>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <div className="text-xs text-slate-500">Tổng phát hành</div>
          <div className="text-2xl font-bold text-amber-400 mt-1">{formatVND(stats.total_points_lifetime || 0)}</div>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <div className="text-xs text-slate-500">Nguồn khách</div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {Object.entries(stats.sources || {}).map(([src, count]) => {
              const cfg = SOURCE_CFG[src] || SOURCE_CFG.all;
              return <span key={src} className={`text-[10px] bg-[#0f172a] border border-[#334155] rounded px-2 py-0.5 ${cfg.color}`}>{cfg.label}: {count}</span>;
            })}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 border-b border-[#334155] pb-px">
        <button onClick={() => setTab("profiles")}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${tab === "profiles" ? "bg-[#1e293b] text-[#F8FAFC] border border-[#334155] border-b-0" : "text-slate-500 hover:text-slate-300"}`}>
          Thành viên ({total})
        </button>
        <button onClick={() => setTab("rules")}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-1.5 ${tab === "rules" ? "bg-[#1e293b] text-[#F8FAFC] border border-[#334155] border-b-0" : "text-slate-500 hover:text-slate-300"}`}>
          <Settings2 className="w-3.5 h-3.5" /> Quy tắc ({rules.length})
        </button>
      </div>

      {/* TAB: Profiles */}
      {tab === "profiles" && (
        <>
          <div className="flex gap-3 items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Tìm SĐT, tên..." className="pl-10 bg-[#0f172a] border-[#334155] text-slate-200 h-9 text-sm" />
            </div>
            <div className="flex gap-1.5">
              {[{ key: "", label: "Tất cả" }, { key: "diamond", label: "Diamond" }, { key: "vip", label: "VIP" }, { key: "normal", label: "Thường" }].map(f => (
                <button key={f.key} onClick={() => { setTierFilter(f.key); setPage(1); }}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${tierFilter === f.key ? "bg-amber-500 text-[#0f172a]" : "bg-[#1e293b] border border-[#334155] text-slate-400 hover:text-slate-200"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
            <div className="divide-y divide-[#334155]/50">
              {isLoading ? <div className="p-8 text-center text-slate-500 text-sm">Đang tải...</div>
              : profiles.length === 0 ? <div className="p-8 text-center text-slate-500 text-sm">Chưa có thành viên</div>
              : profiles.map(p => {
                  const cfg = TIER_CFG[p.tier] || TIER_CFG.normal;
                  const TierIcon = cfg.icon;
                  return (
                    <div key={p.id} className="px-4 py-3 hover:bg-[#334155]/20 transition-colors flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full ${cfg.bg} ${cfg.border} border flex items-center justify-center shrink-0`}>
                        <TierIcon className={`w-5 h-5 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-200 truncate">{p.name || "—"}</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} ${cfg.border} border`}>{cfg.label}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-500">
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone}</span>
                          {p.sources?.map(s => <span key={s} className={(SOURCE_CFG[s] || SOURCE_CFG.all).color}>{(SOURCE_CFG[s] || SOURCE_CFG.all).label}</span>)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-lg font-bold text-lime-400">{p.total_points}</div>
                        <div className="text-[10px] text-slate-600">điểm</div>
                      </div>
                      <div className="text-right shrink-0 w-20">
                        <div className="text-xs text-slate-400">{formatVND(p.total_spent)}đ</div>
                        <div className="text-[10px] text-slate-600">{p.visit_count} lần</div>
                      </div>
                    </div>
                  );
                })}
            </div>
            {total > 30 && (
              <div className="px-4 py-2 border-t border-[#334155] flex items-center justify-between">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="text-xs text-slate-400 disabled:opacity-30">Trước</button>
                <span className="text-xs text-slate-500">Trang {page}/{Math.ceil(total / 30)}</span>
                <button onClick={() => setPage(page + 1)} disabled={page * 30 >= total} className="text-xs text-slate-400 disabled:opacity-30">Sau</button>
              </div>
            )}
          </div>
        </>
      )}

      {/* TAB: Rules */}
      {tab === "rules" && (
        <div className="space-y-3">
          {rules.length === 0 ? (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-8 text-center">
              <Settings2 className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Chưa có quy tắc đổi điểm</p>
              <p className="text-xs text-slate-600 mt-1">Tạo quy tắc để khách hàng đổi điểm lấy ưu đãi</p>
            </div>
          ) : (
            rules.map(rule => {
              const srcCfg = SOURCE_CFG[rule.source] || SOURCE_CFG.all;
              const SrcIcon = srcCfg.icon;
              return (
                <div key={rule.id} className={`bg-[#1e293b] border rounded-xl p-4 transition-colors ${rule.active ? "border-[#334155]" : "border-red-500/20 opacity-60"}`}
                  data-testid={`rule-${rule.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center shrink-0`}>
                        <SrcIcon className={`w-5 h-5 ${srcCfg.color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-200">{rule.name}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${srcCfg.color} border-current/20`}>{srcCfg.label}</span>
                          {!rule.active && <span className="text-[9px] bg-red-500/10 text-red-400 border border-red-500/20 rounded px-1.5 py-0.5">Tắt</span>}
                        </div>
                        {rule.description && <p className="text-xs text-slate-500 mt-0.5">{rule.description}</p>}
                        <div className="flex items-center gap-4 mt-2 text-xs">
                          <span className="text-purple-400 font-bold">{rule.points_required} điểm</span>
                          <span className="text-slate-500">{REWARD_TYPES.find(r => r.value === rule.reward_type)?.label}</span>
                          {rule.reward_value > 0 && <span className="text-lime-400">{rule.reward_type === "discount_percent" ? `${rule.reward_value}%` : `${formatVND(rule.reward_value)}đ`}</span>}
                          {rule.max_per_month > 0 && <span className="text-slate-600">Tối đa {rule.max_per_month}/tháng</span>}
                          {rule.min_tier !== "normal" && <span className="text-amber-400">Từ {TIER_CFG[rule.min_tier]?.label}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => handleToggleRule(rule)}
                        className={`p-1.5 rounded-lg transition-colors ${rule.active ? "text-emerald-400 hover:bg-emerald-500/10" : "text-slate-600 hover:bg-slate-500/10"}`}
                        title={rule.active ? "Tắt" : "Bật"}>
                        {rule.active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      <button onClick={() => openRuleDialog(rule)} className="p-1.5 rounded-lg text-slate-400 hover:bg-[#334155] hover:text-slate-200">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDeleteRule(rule.id)} className="p-1.5 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* TAB: Analytics */}
      {tab === "analytics" && (
        <div className="space-y-4">
          {analyticsLoading ? <div className="p-8 text-center text-slate-500">Đang tải analytics...</div> : (
            <>
              {/* Row 1: RFM Segments + Tier Pie */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-[#F8FAFC] mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-400" /> Phân khúc RFM</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={analytics.segments || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                      <YAxis type="category" dataKey="segment" tick={{ fill: "#e2e8f0", fontSize: 11 }} width={90} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="count" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-[#F8FAFC] mb-3 flex items-center gap-2"><Diamond className="w-4 h-4 text-amber-400" /> Phân bố hạng</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={(analytics.tiers || []).map(t => ({ ...t, name: TIER_CFG[t.tier]?.label || t.tier }))} cx="50%" cy="50%"
                        innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="count" label={({ name, count }) => `${name}: ${count}`}>
                        {(analytics.tiers || []).map((t, i) => (
                          <Cell key={i} fill={t.tier === "diamond" ? "#06b6d4" : t.tier === "vip" ? "#f59e0b" : "#64748b"} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Row 2: Source Usage + Cross-platform */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-[#F8FAFC] mb-3">Nguồn khách & Chi tiêu TB</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={(analytics.sources || []).map(s => ({ ...s, name: (SOURCE_CFG[s.source] || {}).label || s.source }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                      <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}
                        formatter={(v) => formatVND(v) + "đ"} />
                      <Bar dataKey="avg_spent" fill="#84cc16" radius={[4, 4, 0, 0]} name="Chi tiêu TB" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-[#F8FAFC] mb-3">Sử dụng đa nền tảng</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={(analytics.cross_usage || []).map(c => ({ name: `${c.sources} dịch vụ`, count: c.count }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                      <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="count" fill="#a855f7" radius={[4, 4, 0, 0]} name="Số khách" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Row 3: Churn Risk + AI Suggestions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-[#F8FAFC] mb-3 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-400" /> Phân bố nguy cơ rời bỏ</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={(analytics.churn_distribution || []).map(c => {
                      const labels = { "0": "0-20%", "0.2": "20-40%", "0.4": "40-60%", "0.6": "60-80%", "0.8": "80-100%" };
                      return { name: labels[String(c.range)] || c.range, count: c.count };
                    })}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                      <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Số khách">
                        {(analytics.churn_distribution || []).map((_, i) => (
                          <Cell key={i} fill={["#22c55e", "#84cc16", "#f59e0b", "#f97316", "#ef4444"][i] || "#64748b"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-[#F8FAFC] mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> AI gợi ý hành động</h3>
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {(analytics.ai_suggestions || []).map((s, i) => (
                      <div key={i} className="bg-[#0f172a] border border-amber-500/20 rounded-lg p-2.5 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-slate-200">{s.name}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded ${TIER_CFG[s.tier]?.bg || ""} ${TIER_CFG[s.tier]?.color || "text-slate-400"}`}>{TIER_CFG[s.tier]?.label || s.tier}</span>
                        </div>
                        <p className="text-amber-400 mt-1">{s.suggestion}</p>
                        <p className="text-slate-600 mt-0.5">{s.phone} — {formatVND(s.total_spent)}đ tổng chi</p>
                      </div>
                    ))}
                    {(!analytics.ai_suggestions || analytics.ai_suggestions.length === 0) && (
                      <p className="text-slate-500 text-center py-4">Chưa có dữ liệu. Nhấn "Chạy RFM AI" trước.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 4: Top Spenders */}
              <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
                <h3 className="text-sm font-bold text-[#F8FAFC] mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> Top 10 khách VIP</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {(analytics.top_spenders || []).map((p, i) => {
                    const cfg = TIER_CFG[p.tier] || TIER_CFG.normal;
                    return (
                      <div key={p.id} className="flex items-center gap-3 bg-[#0f172a] rounded-lg p-2.5">
                        <span className="text-lg font-bold text-slate-600 w-6 text-center">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold text-slate-200 truncate">{p.name}</span>
                            <span className={`text-[8px] px-1 rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                          </div>
                          <span className="text-[10px] text-slate-500">{p.phone}</span>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-sm font-bold text-lime-400">{formatVND(p.total_spent)}đ</span>
                          <span className="block text-[9px] text-slate-600">{p.visit_count} lần</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Earn dialog */}
      <Dialog open={earnOpen} onOpenChange={setEarnOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2"><Coins className="w-5 h-5 text-lime-400" /> Tích điểm</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">25.000đ = 1 điểm</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input value={earnPhone} onChange={(e) => setEarnPhone(e.target.value)} placeholder="Số điện thoại" className="bg-[#0f172a] border-[#334155] text-slate-200" />
            <Input value={earnAmount} onChange={(e) => setEarnAmount(e.target.value)} placeholder="Số tiền (VNĐ)" type="number" className="bg-[#0f172a] border-[#334155] text-slate-200" />
            <select value={earnSource} onChange={(e) => setEarnSource(e.target.value)} className="w-full bg-[#0f172a] border border-[#334155] text-slate-200 rounded-md h-10 px-3 text-sm">
              {Object.entries(SOURCE_CFG).filter(([k]) => k !== "all").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            {earnAmount && <p className="text-xs text-lime-400">= {Math.floor(parseInt(earnAmount || 0) / 25000)} điểm</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEarnOpen(false)} className="text-slate-400">Huỷ</Button>
            <Button onClick={handleEarn} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-bold">Tích điểm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redeem dialog */}
      <Dialog open={redeemOpen} onOpenChange={setRedeemOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2"><Gift className="w-5 h-5 text-amber-400" /> Đổi điểm</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">1 điểm = 1.000đ</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input value={redeemPhone} onChange={(e) => setRedeemPhone(e.target.value)} placeholder="Số điện thoại" className="bg-[#0f172a] border-[#334155] text-slate-200" />
            <Input value={redeemPoints} onChange={(e) => setRedeemPoints(e.target.value)} placeholder="Số điểm đổi" type="number" className="bg-[#0f172a] border-[#334155] text-slate-200" />
            {redeemPoints && <p className="text-xs text-amber-400">= {formatVND(parseInt(redeemPoints || 0) * 1000)}đ</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRedeemOpen(false)} className="text-slate-400">Huỷ</Button>
            <Button onClick={handleRedeem} className="bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-bold">Đổi điểm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2"><Upload className="w-5 h-5 text-sky-400" /> Import Excel</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">Cần cột SĐT. Trùng SĐT sẽ merge.</DialogDescription>
          </DialogHeader>
          <div className="py-3"><input ref={fileRef} type="file" accept=".xlsx,.xls" className="text-sm text-slate-400" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImportOpen(false)} className="text-slate-400">Huỷ</Button>
            <Button onClick={handleImport} disabled={importing} className="bg-sky-500 hover:bg-sky-600 text-white font-bold">{importing ? "Đang import..." : "Import"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule create/edit dialog */}
      <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-purple-400" /> {editingRule ? "Sửa quy tắc" : "Thêm quy tắc đổi điểm"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Tên quy tắc *</label>
              <Input value={rName} onChange={(e) => setRName(e.target.value)} placeholder="VD: Miễn phí cuốc xe" className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Mô tả</label>
              <Textarea value={rDesc} onChange={(e) => setRDesc(e.target.value)} placeholder="Mô tả chi tiết..." className="bg-[#0f172a] border-[#334155] text-slate-200 h-16" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Áp dụng cho</label>
                <select value={rSource} onChange={(e) => setRSource(e.target.value)} className="w-full bg-[#0f172a] border border-[#334155] text-slate-200 rounded-md h-10 px-3 text-sm">
                  {Object.entries(SOURCE_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Điểm cần *</label>
                <Input value={rPoints} onChange={(e) => setRPoints(e.target.value)} type="number" placeholder="10" className="bg-[#0f172a] border-[#334155] text-slate-200" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Loại thưởng</label>
                <select value={rRewardType} onChange={(e) => setRRewardType(e.target.value)} className="w-full bg-[#0f172a] border border-[#334155] text-slate-200 rounded-md h-10 px-3 text-sm">
                  {REWARD_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Giá trị</label>
                <Input value={rRewardValue} onChange={(e) => setRRewardValue(e.target.value)} type="number" placeholder="20000" className="bg-[#0f172a] border-[#334155] text-slate-200" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Nhãn hiển thị (cho khách)</label>
              <Input value={rRewardLabel} onChange={(e) => setRRewardLabel(e.target.value)} placeholder="VD: Giảm 20.000đ cuốc xe" className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Tối đa / tháng</label>
                <Input value={rMaxMonth} onChange={(e) => setRMaxMonth(e.target.value)} type="number" placeholder="0 = không giới hạn" className="bg-[#0f172a] border-[#334155] text-slate-200" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Hạng tối thiểu</label>
                <select value={rMinTier} onChange={(e) => setRMinTier(e.target.value)} className="w-full bg-[#0f172a] border border-[#334155] text-slate-200 rounded-md h-10 px-3 text-sm">
                  <option value="normal">Thường</option>
                  <option value="vip">VIP</option>
                  <option value="diamond">Diamond</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRuleDialogOpen(false)} className="text-slate-400">Huỷ</Button>
            <Button onClick={handleSaveRule} className="bg-purple-500 hover:bg-purple-400 text-white font-bold">{editingRule ? "Cập nhật" : "Tạo quy tắc"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
